import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Link2, Loader2, Pencil, RefreshCw, Trash2, UserCheck, Circle, MessageSquareText } from 'lucide-react';
import { OnboardMessageDialog } from '../components/prospects/OnboardMessageDialog';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getStoredUser, prospects } from '../api';
import { useToast } from '../hooks/use-toast';
import { ProspectDialog } from '../components/prospects/ProspectDialog';
import { DocumentsPanel } from '../components/prospects/DocumentsPanel';
import { FicaPanel } from '../components/prospects/FicaPanel';
import { InfoForm } from '../components/prospects/InfoForm';
import { STAGES, STAGE_LABEL, zar, errMsg } from '../components/prospects/constants';

const Step = ({ done, label, testid }) => (
  <div className="flex items-center gap-2 text-sm" data-testid={testid}>
    {done ? <span className="h-5 w-5 rounded-full bg-emerald-600 text-white flex items-center justify-center"><Check size={12}/></span> : <Circle size={20} className="text-stone-300"/>}
    <span className={done ? 'text-stone-900' : 'text-stone-500'}>{label}</span>
  </div>
);

const Overview = ({ p }) => (
  <div className="grid lg:grid-cols-2 gap-5">
    <div className="rounded-xl bg-white border border-stone-200 p-5 space-y-3 text-sm" data-testid="prospect-overview">
      {[['Entity type', p.entityType], ['Contact', p.contact], ['Email', p.email], ['Cell phone', p.phone], ['Lead source', p.source],
        ['Services', (p.services || []).join(', ')], ['Monthly fee', `${zar(p.monthlyFee)} excl. VAT`], ['Once-off fee', zar(p.onceOffFee)]].map(([k, v]) => (
        <div key={k} className="flex gap-4"><div className="w-32 shrink-0 text-stone-500">{k}</div><div className="text-stone-900">{v || '—'}</div></div>
      ))}
      {p.notes && <div className="pt-3 border-t border-stone-100 whitespace-pre-wrap text-stone-700">{p.notes}</div>}
      {p.lostReason && <div className="pt-3 border-t border-stone-100 text-red-700">Lost: {p.lostReason}</div>}
    </div>
    <div className="rounded-xl bg-white border border-stone-200 p-5" data-testid="prospect-history">
      <div className="font-semibold text-stone-900 mb-3">Journey</div>
      <ol className="relative border-l border-stone-200 ml-2 space-y-4">
        {[...(p.history || [])].reverse().map((h, i) => (
          <li key={i} className="ml-4">
            <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-emerald-600 ring-4 ring-white"/>
            <div className="text-sm font-medium text-stone-900">{STAGE_LABEL[h.stage]}</div>
            <div className="text-xs text-stone-500">{new Date(h.at).toLocaleString()} · {h.by}{h.note ? ` · ${h.note}` : ''}</div>
          </li>
        ))}
      </ol>
    </div>
  </div>
);

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user] = useState(getStoredUser());
  const [p, setP] = useState(null);
  const [info, setInfo] = useState(null);
  const [edit, setEdit] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => prospects.get(id).then((x) => { setP(x); setInfo(x.info || {}); }).catch(() => navigate('/dashboard/prospects'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);
  const update = (x) => { setP(x); setInfo(x.info || {}); };
  const fail = (t) => (e) => toast({ title: t, description: errMsg(e), variant: 'destructive' });

  if (!p) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-stone-400"/></div>;
  const link = `${window.location.origin}/onboard/${p.token}`;
  const converted = p.stage === 'converted';
  const isOwner = user?.firmRole === 'owner';

  const stage = (s) => {
    const reason = s === 'lost' ? window.prompt('Why was this prospect lost? (optional)') : '';
    if (reason === null) return;
    prospects.stage(p.id, s, reason || '').then(update).catch(fail('Could not change stage'));
  };
  const copy = () => navigator.clipboard.writeText(link).then(() => toast({ title: 'Onboarding link copied', description: 'Send it to the prospect by WhatsApp or email.' }));
  const convert = () => {
    const override = !p.ready;
    if (override && !window.confirm('Onboarding is not complete (documents unsigned or FICA outstanding). Convert anyway as owner override?')) return;
    if (!override && !window.confirm(`Convert ${p.name} to a client? This creates the client, schedules their compliance tasks and opens their portal.`)) return;
    setBusy(true);
    prospects.convert(p.id, override).then((r) => { toast({ title: 'Converted to client', description: 'Tasks scheduled and documents moved across.' }); navigate(`/dashboard/clients/${r.clientId}`); })
      .catch(fail('Could not convert')).finally(() => setBusy(false));
  };
  const remove = () => window.confirm(`Delete prospect ${p.name} and all their onboarding files?`) &&
    prospects.remove(p.id).then(() => { toast({ title: 'Prospect deleted' }); navigate('/dashboard/prospects'); }).catch(fail('Could not delete'));

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Prospects"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <Link to="/dashboard/prospects" className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-emerald-800" data-testid="prospect-back-link"><ArrowLeft size={15}/> All prospects</Link>
          <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900" data-testid="prospect-name">{p.name}</h1>
              <p className="mt-1 text-sm text-stone-600">{p.entityType} · {zar(p.monthlyFee)}/mo · added {new Date(p.createdAt).toLocaleDateString()}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {converted ? (
                <Link to={`/dashboard/clients/${p.convertedClientId}`}><Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="prospect-open-client-btn"><UserCheck size={15} className="mr-2"/> Open client</Button></Link>
              ) : (
                <>
                  <Select value={p.stage} onValueChange={stage}>
                    <SelectTrigger className="h-10 w-48 bg-white" data-testid="prospect-stage-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{STAGES.filter((s) => s.key !== 'converted').map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="outline" className="h-10" onClick={() => setEdit(true)} data-testid="prospect-edit-btn"><Pencil size={14} className="mr-2"/> Edit</Button>
                  <Button variant="outline" className="h-10 text-red-700 hover:text-red-800" onClick={remove} data-testid="prospect-delete-btn"><Trash2 size={14}/></Button>
                  <Button disabled={busy || (!p.ready && !isOwner)} onClick={convert} className="h-10 bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="prospect-convert-btn">
                    {busy ? <Loader2 size={14} className="mr-2 animate-spin"/> : <UserCheck size={15} className="mr-2"/>} Convert to client
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_340px] gap-5">
            <div className="rounded-xl bg-white border border-stone-200 p-5 flex flex-wrap gap-x-8 gap-y-3" data-testid="prospect-readiness">
              <Step done={p.progress.docsSent > 0} label="Documents sent" testid="step-sent"/>
              <Step done={p.progress.allSigned} label={`Signed ${p.progress.docsSigned}/${p.progress.docsTotal}`} testid="step-signed"/>
              <Step done={!!(p.info && p.info.registeredName)} label="Details captured" testid="step-info"/>
              <Step done={p.progress.ficaComplete} label={`FICA ${p.progress.ficaDone}/${p.progress.ficaRequired}`} testid="step-fica"/>
              <Step done={converted} label={converted ? 'Converted' : p.ready ? 'Ready to convert' : 'Convert'} testid="step-converted"/>
            </div>
            <div className="rounded-xl bg-emerald-950 text-white p-5" data-testid="prospect-link-card">
              <div className="text-xs uppercase tracking-wider text-emerald-300 flex items-center gap-1.5"><Link2 size={13}/> Onboarding link</div>
              <div className="mt-2 text-xs break-all text-emerald-100/80" data-testid="prospect-link">{link}</div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={copy} className="bg-white text-emerald-900 hover:bg-emerald-50" data-testid="prospect-copy-link-btn"><Copy size={13} className="mr-1.5"/> Copy</Button>
                <Button size="sm" onClick={() => setMsgOpen(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="prospect-message-btn"><MessageSquareText size={13} className="mr-1.5"/> Message</Button>
                <Button size="sm" variant="ghost" onClick={() => window.confirm('Create a new link? The old link will stop working.') && prospects.regenerateLink(p.id).then(update)} className="text-emerald-100 hover:bg-emerald-900 hover:text-white" data-testid="prospect-regen-link-btn"><RefreshCw size={13} className="mr-1.5"/> New link</Button>
              </div>
            </div>
          </div>

          <Tabs defaultValue="overview">
            <TabsList className="bg-white border border-stone-200">
              <TabsTrigger value="overview" data-testid="prospect-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="documents" data-testid="prospect-tab-documents">Engagement documents</TabsTrigger>
              <TabsTrigger value="fica" data-testid="prospect-tab-fica">FICA</TabsTrigger>
              <TabsTrigger value="info" data-testid="prospect-tab-info">Client details</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="mt-5"><Overview p={p}/></TabsContent>
            <TabsContent value="documents" className="mt-5"><DocumentsPanel p={p} onChange={update} toast={toast}/></TabsContent>
            <TabsContent value="fica" className="mt-5"><FicaPanel p={p} onChange={update} toast={toast}/></TabsContent>
            <TabsContent value="info" className="mt-5">
              <div className="rounded-xl bg-white border border-stone-200 p-5">
                <p className="text-xs text-stone-500 mb-4">Filled in by the prospect on their onboarding link. You can complete or correct it here. It is used for the documents and the client record.</p>
                <InfoForm value={info} onChange={setInfo} needsDebit={(p.documents || []).some((d) => d.key === 'debit_order')}/>
                <div className="mt-5 flex justify-end">
                  <Button onClick={() => prospects.saveInfo(p.id, info).then((x) => { update(x); toast({ title: 'Details saved' }); }).catch(fail('Could not save'))} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="prospect-info-save-btn">Save details</Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>
      <ProspectDialog open={edit} onOpenChange={setEdit} prospect={p} onSaved={update} toast={toast}/>
      <OnboardMessageDialog open={msgOpen} onOpenChange={setMsgOpen} prospect={p} link={link} user={user} toast={toast}/>
    </div>
  );
}
